import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Switch,
    CircularProgress,
    Box,
    Grid,
    Tooltip,
    IconButton,
    Paper,
    Typography,
    Table,
    TableRow,
    TableCell,
    TableHead,
    TableBody,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import {
    getKycPlanByIdAction,
    createKycPlanAction,
    updateKycPlanAction,
} from "../../Actions/KycPlanManagemantAction";
import { MyInfoIcon } from "../../Assets/svg/MyIcons";
import CloseIcon from "@mui/icons-material/Close";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { useMemo } from "react";

const { VALIDATION } = FRONTEND_MESSAGES;

const addonSchema = Yup.object({
    is_enabled: Yup.boolean(),
    unit_price: Yup.number()
        .typeError("Unit price must be a number")
        .when("is_enabled", {
            is: true,
            then: (s) =>
                s.required("Unit price is required").positive("Price must be positive"),
            otherwise: (s) => s.notRequired().nullable()
        })
});

const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .matches(/^[a-zA-Z0-9\s&-]+$/, VALIDATION.INVALID_FORMAT("Plan name"))
        .max(50, VALIDATION.MAX_LENGTH("Plan name", 50))
        .required(VALIDATION.REQUIRED_FIELD("Plan name")),
    description: Yup.string()
        .trim()
        .matches(/^[a-zA-Z0-9\s&.,()-]+$/, VALIDATION.INVALID_FORMAT("Description"))
        .max(500, VALIDATION.MAX_LENGTH("Description", 500))
        .required(VALIDATION.REQUIRED_FIELD("Description")),
    is_kyc_enabled: Yup.boolean(),
    is_aml_enabled: Yup.boolean(),
    is_selfie_enabled: Yup.boolean(),
    is_ongoing_aml_enabled: Yup.boolean(),
    is_portal_enabled: Yup.boolean().test(
        "portal-check",
        "At least one of KYC or AML must be enabled",
        function (value) {
            const { portal_kyc_enabled, portal_aml_enabled } = this.parent;

            if (!value) return true;

            return portal_kyc_enabled || portal_aml_enabled;
        }
    ),
    portal_kyc_enabled: Yup.boolean(),
    portal_selfie_enabled: Yup.boolean(),
    portal_aml_enabled: Yup.boolean(),
    monthly_base_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Base price"))
        .positive(VALIDATION.POSITIVE_ONLY("Base price"))
        .max(999999999, VALIDATION.MAX_VALUE("Base price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Base price")),
    monthly_included_verification: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Included verifications"))
        .min(0, VALIDATION.MIN_VALUE("Included verifications", 0))
        .required(VALIDATION.REQUIRED_FIELD("Included verifications")),
    yearly_base_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Base price"))
        .positive(VALIDATION.POSITIVE_ONLY("Base price"))
        .max(999999999, VALIDATION.MAX_VALUE("Base price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Base price")),
    yearly_included_verification: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Included verifications"))
        .min(0, VALIDATION.MIN_VALUE("Included verifications", 0))
        .required(VALIDATION.REQUIRED_FIELD("Included verifications")),
    per_verification_price: Yup.number()
        .typeError(VALIDATION.NUMERIC_ONLY("Per verification price"))
        .positive(VALIDATION.POSITIVE_ONLY("Per verification price"))
        .max(999999999, VALIDATION.MAX_VALUE("Per verification price", 999999999))
        .required(VALIDATION.REQUIRED_FIELD("Per verification price")),
    is_custom: Yup.boolean(),
    isActive: Yup.boolean(),
    addons: Yup.object({
        aml_match: addonSchema,
        aml_ongoing_check: addonSchema
    })
});

// Helper: Compare old vs new values (Deep comparison)
const getChangedPlanFields = (oldData, newData) => {
    const changes = {};
    if (!oldData) return changes;

    Object.keys(newData).forEach((key) => {
        const oldVal = oldData[key];
        const newVal = newData[key];

        const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
        const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

        if (oldIsEmpty && newIsEmpty) return;

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes[key] = {
                old: oldVal,
                new: newVal,
            };
        }
    });

    return changes;
};

const AddEditKycPlan = ({
    open,
    handleClose,
    id,
    refetchPlanList = () => { },
    handleToast = () => { },
    changePortalFilter,
}) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(null);
    const [changedFields, setChangedFields] = useState({});

    const formik = useFormik({
        initialValues: {
            name: "",
            description: "",
            is_kyc_enabled: false,
            is_aml_enabled: false,
            is_selfie_enabled: false,
            is_ongoing_aml_enabled: false,
            is_portal_enabled: false,
            portal_kyc_enabled: false,
            portal_selfie_enabled: false,
            portal_aml_enabled: false,
            monthly_base_price: "",
            monthly_included_verification: "",
            yearly_base_price: "",
            yearly_included_verification: "",
            per_verification_price: "",
            is_custom: false,
            isActive: true,
            addons: {
                aml_match: {
                    is_enabled: false,
                    unit_price: ""
                },
                aml_ongoing_check: {
                    is_enabled: false,
                    unit_price: ""
                }
            }
        },
        validationSchema,
        validate: (values) => {
            const errors = {};

            if (
                values.is_portal_enabled &&
                !values.portal_kyc_enabled &&
                !values.portal_aml_enabled
            ) {
                errors.is_portal_enabled = "At least one of KYC or AML must be enabled";
            }

            return errors;
        },
        onSubmit: async (values) => {
            const addonsArray = [
                {
                    addon_type: "aml_match",
                    unit_price: values.addons.aml_match.unit_price || 0,
                    is_enabled: values.addons.aml_match.is_enabled
                },
                {
                    addon_type: "aml_ongoing_check",
                    unit_price: values.addons.aml_ongoing_check.unit_price || 0,
                    is_enabled: values.addons.aml_ongoing_check.is_enabled
                }
            ];
            const payload = {
                ...values,
                name: values.name?.trim(),
                description: values.description?.trim(),
                addons: addonsArray
            };

            if (id) {
                payload.changedFields = JSON.stringify(Object.keys(changedFields));
                payload.comparisonData = JSON.stringify(changedFields);
            }

            setLoading(true);
            try {
                if (id) {
                    const response = await updateKycPlanAction(id, payload);
                    const { message } = response?.data || {};
                    handleToast("success", message);
                } else {
                    const response = await createKycPlanAction(payload);
                    const { message } = response?.data || {};
                    changePortalFilter("kyc");
                    handleToast("success", message);
                }
                handleClose();
            } catch (err) {
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    (id
                        ? "Something went wrong while updating plan"
                        : "Something went wrong while adding plan");

                handleToast("error", message);
                console.error("❌ Error saving plan:", err);
            } finally {
                setLoading(false);
                refetchPlanList();
            }
        },
    });

    useEffect(() => {
        const fetchPlan = async () => {
            if (!id) return;
            setFetching(true);
            try {
                const response = await getKycPlanByIdAction(id);
                const data = response?.data?.data || {};

                const addonMap = {
                    aml_match: { is_enabled: false, unit_price: "" },
                    aml_ongoing_check: { is_enabled: false, unit_price: "" }
                };

                data?.addons?.forEach(addon => {
                    if (addonMap[addon.addon_type]) {
                        addonMap[addon.addon_type] = {
                            is_enabled: addon.is_enabled,
                            unit_price: addon.is_enabled ? addon.unit_price : ""
                        };
                    }
                });

                const initialValuesToSet = {
                    name: data.name || "",
                    description: data.description || "",
                    is_kyc_enabled: data.is_kyc_enabled ?? false,
                    is_aml_enabled: data.is_aml_enabled ?? false,
                    is_selfie_enabled: data.is_selfie_enabled ?? false,
                    is_ongoing_aml_enabled: data.is_ongoing_aml_enabled ?? false,
                    is_portal_enabled: data.is_portal_enabled ?? false,
                    portal_kyc_enabled: data.portal_kyc_enabled ?? false,
                    portal_selfie_enabled: data.portal_selfie_enabled ?? false,
                    portal_aml_enabled: data.portal_aml_enabled ?? false,
                    monthly_base_price: data.monthly_base_price ?? "",
                    monthly_included_verification: data.monthly_included_verification ?? "",
                    yearly_base_price: data.yearly_base_price ?? "",
                    yearly_included_verification: data.yearly_included_verification ?? "",
                    per_verification_price: data.per_verification_price ?? "",
                    is_custom: data.is_custom ?? false,
                    isActive: data.isActive ?? true,
                    addons: addonMap
                };

                formik.resetForm({
                    values: initialValuesToSet,
                });
                setInitialFormValues(initialValuesToSet);

            } catch (err) {
                console.error("❌ Error fetching plan:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchPlan();
    }, [id]);

    // Track changed fields for audit/update logging
    useEffect(() => {
        if (!id || !initialFormValues) return;

        const changed = getChangedPlanFields(initialFormValues, formik.values);
        setChangedFields(changed);
    }, [formik.values, initialFormValues, id]);

    const handleTextChange = (e) => {
        formik.handleChange(e);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleNumberChange = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        formik.setFieldValue(e.target.name, value);
        formik.setFieldTouched(e.target.name, true, false);
    };

    const SectionHeader = ({ title }) => (
        <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
        >
            {title}
        </Typography>
    );

    const handleToggle = (key) => (e) => {
        const value = e.target.checked;

        formik.setFieldValue(key, value, true);

        // Portal logic
        if (key === "is_portal_enabled" && !value) {
            formik.setFieldValue("portal_kyc_enabled", false);
            formik.setFieldValue("portal_aml_enabled", false);
        }

        // Portal KYC logic
        if (key === "portal_kyc_enabled" && !value) {
            formik.setFieldValue("portal_selfie_enabled", false);
        }

        // KYC logic
        if (key === "is_kyc_enabled" && !value) {
            formik.setFieldValue("is_aml_enabled", false);
            formik.setFieldValue("is_ongoing_aml_enabled", false);
            formik.setFieldValue("is_selfie_enabled", false);
        }

        // AML logic
        if (key === "is_aml_enabled" && !value) {
            formik.setFieldValue("is_ongoing_aml_enabled", false);
        }
    }


    const hasFeatureError = useMemo(() => {
        const allowedFeaturesValues = formik.values.allowedFeatures || {};
        const hasAtLeastOneFeature = allowedFeaturesValues.user_chat === true || allowedFeaturesValues.support_tickets === true;
        const hasError = formik.errors.allowedFeatures;
        return !hasAtLeastOneFeature && hasError
            ? "error.main"
            : "rgba(25, 118, 210, 0.12)";
    }, [formik.values.allowedFeatures, formik.errors.allowedFeatures]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                className="text-bg-primary"
                sx={{
                    py: 2.5,
                    px: 3,
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    position: "relative",
                }}
            >
                {id ? "Update KYC Plan" : "Create New KYC Plan"}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "#fff",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {fetching ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={400}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent sx={{ px: 3, py: 3 }}>
                        {/* Plan Information Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Plan Information" />
                            <Grid container spacing={2.5}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Plan Name<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter plan name"
                                        name="name"
                                        size="small"
                                        fullWidth
                                        disabled={id}
                                        value={formik.values.name}
                                        onChange={handleTextChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.name &&
                                            Boolean(formik.errors.name)
                                        }
                                        helperText={
                                            formik.touched.name &&
                                            formik.errors.name
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Description<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter description"
                                        name="description"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={formik.values.description}
                                        onChange={handleTextChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.description &&
                                            Boolean(formik.errors.description)
                                        }
                                        helperText={
                                            formik.touched.description &&
                                            formik.errors.description
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Custom Section and Status Section*/}

                        <Grid container spacing={3}>
                            {/* Custom Plan */}
                            <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: "grey.50",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: 2,
                                        height: "100%",
                                    }}
                                >
                                    <SectionHeader title="Custom Plan" />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formik.values.is_custom}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        "is_custom",
                                                        e.target.checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {formik.values.is_custom
                                                        ? "Custom"
                                                        : "Standard"}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formik.values.is_custom
                                                        ? "custom plan"
                                                        : "standard plan with predefined features and limits"}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>

                            {/* Plan Status */}
                            <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: "grey.50",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: 2,
                                        height: "100%",
                                    }}
                                >
                                    <SectionHeader title="Plan Status" />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formik.values.isActive}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        "isActive",
                                                        e.target.checked
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {formik.values.isActive
                                                        ? "Active"
                                                        : "Inactive"}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formik.values.isActive
                                                        ? "This plan is available for users to subscribe"
                                                        : "This plan is hidden from users"}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Paper>
                            </Grid>
                        </Grid>

                        { }
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Base Features" />
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    bgcolor: "rgba(25, 118, 210, 0.04)",
                                    border: "1px solid",
                                    borderColor: hasFeatureError,
                                }}
                            >
                                {/* KYC */}
                                <Box
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 2,
                                        bgcolor: "background.paper",
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography>KYC Enabled</Typography>

                                    <Switch
                                        checked={formik.values.is_kyc_enabled}
                                        onChange={handleToggle("is_kyc_enabled")}
                                    />
                                </Box>

                                {/* AML (child of KYC) */}
                                {formik.values.is_kyc_enabled && (
                                    <Box sx={{ pl: 3 }}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2,
                                                bgcolor: "background.paper",
                                                border: "1px solid",
                                                borderColor: "grey.300",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography>AML Enabled</Typography>

                                            <Switch
                                                checked={formik.values.is_aml_enabled}
                                                onChange={handleToggle("is_aml_enabled")}
                                            />
                                        </Box>

                                        {/* Ongoing AML (child of AML) */}
                                        {formik.values.is_aml_enabled && (
                                            <Box sx={{ pl: 3 }}>
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        mb: 2,
                                                        borderRadius: 2,
                                                        bgcolor: "background.paper",
                                                        border: "1px solid",
                                                        borderColor: "grey.300",
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Typography>Ongoing AML</Typography>

                                                    <Switch
                                                        checked={formik.values.is_ongoing_aml_enabled}
                                                        onChange={handleToggle("is_ongoing_aml_enabled")}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Selfie (child of KYC) */}
                                {formik.values.is_kyc_enabled && (
                                    <Box sx={{ pl: 3 }}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                borderRadius: 2,
                                                bgcolor: "background.paper",
                                                border: "1px solid",
                                                borderColor: "grey.300",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography>Selfie Enabled</Typography>

                                            <Switch
                                                checked={formik.values.is_selfie_enabled}
                                                onChange={handleToggle("is_selfie_enabled")}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        {/* portal feature */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Portal enabled features" />
                            {/* Portals */}
                            <Box>
                                {/* Outer container */}
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "rgba(25, 118, 210, 0.04)",
                                        border: "1px solid",
                                        borderColor: hasFeatureError,
                                    }}
                                >
                                    {/* ===== Portal Enabled (Main Card) ===== */}
                                    <Box
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            borderRadius: 2,
                                            bgcolor: "background.paper",
                                            border: "1px solid",
                                            borderColor: "grey.300",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography>Portals Enabled</Typography>

                                        <Switch
                                            checked={formik.values.is_portal_enabled}
                                            onChange={handleToggle("is_portal_enabled")}
                                        />
                                    </Box>

                                    {/*Sub features*/}
                                    {formik.values.is_portal_enabled && (
                                        <Box sx={{ pl: 3 }}>
                                            {/* KYC */}
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    mb: 2,
                                                    borderRadius: 2,
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "grey.300",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography>KYC Enable</Typography>

                                                <Switch
                                                    checked={formik.values.portal_kyc_enabled}
                                                    onChange={handleToggle("portal_kyc_enabled")}
                                                />
                                            </Box>
                                            {/* Selfie (child of KYC) */}
                                            {formik.values.portal_kyc_enabled && (
                                                <Box sx={{ pl: 3 }}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            mb: 2,
                                                            borderRadius: 2,
                                                            bgcolor: "background.paper",
                                                            border: "1px solid",
                                                            borderColor: "grey.300",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography>Selfie Enabled</Typography>

                                                        <Switch
                                                            checked={formik.values.portal_selfie_enabled}
                                                            onChange={handleToggle("portal_selfie_enabled")}
                                                        />
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* AML */}
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: "background.paper",
                                                    border: "1px solid",
                                                    borderColor: "grey.300",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography>AML Enable</Typography>

                                                <Switch
                                                    checked={formik.values.portal_aml_enabled}
                                                    onChange={handleToggle("portal_aml_enabled")}
                                                />
                                            </Box>

                                            {/* Error */}
                                            {formik.errors.is_portal_enabled && (
                                                <Typography color="error" variant="caption">
                                                    {formik.errors.is_portal_enabled}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Paper>

                        {/* Pricing Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Pricing and Verifications" />
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Monthly Base Price<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter monthly base price"
                                        name="monthly_base_price"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.monthly_base_price}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.monthly_base_price &&
                                            Boolean(formik.errors.monthly_base_price)
                                        }
                                        helperText={
                                            formik.touched.monthly_base_price &&
                                            formik.errors.monthly_base_price
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Monthly Included Verifications<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter monthly included verifications"
                                        name="monthly_included_verification"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.monthly_included_verification}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.monthly_included_verification &&
                                            Boolean(formik.errors.monthly_included_verification)
                                        }
                                        helperText={
                                            formik.touched.monthly_included_verification &&
                                            formik.errors.monthly_included_verification
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Yearly Base Price<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter yearly base price"
                                        name="yearly_base_price"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.yearly_base_price}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.yearly_base_price &&
                                            Boolean(formik.errors.yearly_base_price)
                                        }
                                        helperText={
                                            formik.touched.yearly_base_price &&
                                            formik.errors.yearly_base_price
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Yearly Included Verifications<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter yearly included verifications"
                                        name="yearly_included_verification"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.yearly_included_verification}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.yearly_included_verification &&
                                            Boolean(formik.errors.yearly_included_verification)
                                        }
                                        helperText={
                                            formik.touched.yearly_included_verification &&
                                            formik.errors.yearly_included_verification
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>Per Verifications Price<span className="text-danger ms-1">*</span> </Typography>
                                    <TextField
                                        placeholder="Enter per verifications price"
                                        name="per_verification_price"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formik.values.per_verification_price}
                                        onChange={handleNumberChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.per_verification_price &&
                                            Boolean(formik.errors.per_verification_price)
                                        }
                                        helperText={
                                            formik.touched.per_verification_price &&
                                            formik.errors.per_verification_price
                                        }
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        mr: 1,
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                </Box>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Add Ons Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 3,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 2,
                            }}
                        >
                            <SectionHeader title="Add-ons Pricing" />

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Add-on</b></TableCell>
                                        <TableCell align="center"><b>Enabled</b></TableCell>
                                        <TableCell align="center"><b>Unit Price</b></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>

                                    {/* AML Ongoing Check */}
                                    <TableRow>
                                        <TableCell>AML Ongoing Check</TableCell>

                                        <TableCell align="center">
                                            <Switch
                                                checked={formik.values.addons?.aml_ongoing_check?.is_enabled}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    formik.setFieldValue("addons.aml_ongoing_check.is_enabled", checked);

                                                    if (!checked) {
                                                        formik.setFieldValue("addons.aml_ongoing_check.unit_price", "");
                                                    } else {
                                                        formik.setFieldTouched("addons.aml_ongoing_check.unit_price", true);
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <TextField
                                                name="addons.aml_ongoing_check.unit_price"
                                                size="small"
                                                type="number"
                                                disabled={!formik.values.addons?.aml_ongoing_check?.is_enabled}
                                                value={
                                                    formik.values.addons?.aml_ongoing_check?.is_enabled
                                                        ? formik.values.addons?.aml_ongoing_check?.unit_price
                                                        : ""
                                                }
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        "addons.aml_ongoing_check.unit_price",
                                                        e.target.value
                                                    );
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.addons?.aml_ongoing_check?.unit_price &&
                                                    Boolean(formik.errors.addons?.aml_ongoing_check?.unit_price)
                                                }
                                                helperText={
                                                    formik.touched.addons?.aml_ongoing_check?.unit_price &&
                                                    formik.errors.addons?.aml_ongoing_check?.unit_price
                                                }
                                                sx={{
                                                    width: 120,
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: formik.values.addons?.aml_ongoing_check?.is_enabled
                                                                ? "primary.main"
                                                                : "grey.300"
                                                        }
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>

                                    {/* AML Match */}
                                    <TableRow>
                                        <TableCell>AML Match</TableCell>

                                        <TableCell align="center">
                                            <Switch
                                                checked={formik.values.addons?.aml_match?.is_enabled}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    formik.setFieldValue("addons.aml_match.is_enabled", checked);

                                                    if (!checked) {
                                                        formik.setFieldValue("addons.aml_match.unit_price", "");
                                                    } else {
                                                        formik.setFieldTouched("addons.aml_match.unit_price", true);
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <TextField
                                                name="addons.aml_match.unit_price"
                                                size="small"
                                                type="number"
                                                disabled={!formik.values.addons?.aml_match?.is_enabled}
                                                value={
                                                    formik.values.addons?.aml_match?.is_enabled
                                                        ? formik.values.addons?.aml_match?.unit_price
                                                        : ""
                                                }
                                                onChange={(e) => {
                                                    formik.setFieldValue(
                                                        "addons.aml_match.unit_price",
                                                        e.target.value
                                                    );
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.addons?.aml_match?.unit_price &&
                                                    Boolean(formik.errors.addons?.aml_match?.unit_price)
                                                }
                                                helperText={
                                                    formik.touched.addons?.aml_match?.unit_price &&
                                                    formik.errors.addons?.aml_match?.unit_price
                                                }
                                                sx={{
                                                    width: 120,
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: formik.values.addons?.aml_match?.is_enabled
                                                                ? "primary.main"
                                                                : "grey.300"
                                                        }
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>

                                </TableBody>
                            </Table>
                        </Paper>

                    </DialogContent>
                    <DialogActions className="border-top p-7 d-flex justify-content-between">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn btn-sm btn-outline btn-outline-primary"
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="btn btn-sm btn-primary"
                        >
                            {id ? "Update" : "Create"} Plan
                            {loading && (
                                <span
                                    className="spinner-border spinner-border-sm ms-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                            )}
                        </button>
                    </DialogActions>
                </form>
            )}
        </Dialog>
    );
};

export default AddEditKycPlan;
